import {
  WSAlert,
  WSButton,
  WSCard,
  WSDataItem,
  WSEmptyState,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Batch, BatchStatus, BatchSummary } from "@wingspanhq/wingspan-api-sdk";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

import {
  PATH_PAYABLES_DEDUCTIONS,
  PATH_PAYABLES_TAB
} from "../../../Payables/paths";
import { toURLQuerySearch } from "../../../../shared/hooks/useUrlQuery";
import { useDefaultCurrency } from "../../../../shared/hooks/useDefaultCurrency";
import { ImportBatchProgress } from "../../components/ImportBatchProgress";
import { ImportBatchUploadFailedAlert } from "../../components/ImportBatchUploadFailedAlert";
import { ImportBatchUploadProgress } from "../../components/ImportBatchUploadProgress";
import { ImportBatchUploadRejectionsAlert } from "../../components/ImportBatchUploadRejectionsAlert";
import { ImportBatchUploadUnuploadedRowsAlert } from "../../components/ImportBatchUploadUnuploadedRowsAlert";
import { useModalPayablesColumnsGuide } from "../../components/ModalPayableColumnsGuide";
import { PAYABLES_SHEET_SLUG } from "../../constants";
import { useFailedBatchItems } from "../../hooks/useFailedBatchItems";
import { useFlatfileUploadJobStatus } from "../../hooks/useFlatfileUploadJobStatus";
import { useImportBatchStatus } from "../../hooks/useImportBatchStatus";
import { useImportBatchStatusActions } from "../../hooks/useImportBatchActions";
import { useSkippedRowsCount } from "../../hooks/useSkippedRowsCount";
import { selectorImportBatchUploadedFilename } from "../../selectors/selectorImportBatchUploadedFilename";
import { selectorIsImportBatchFileUploaded } from "../../selectors/selectorIsImportBatchFileUploaded";
import { selectorIsImportBatchFullyImported } from "../../selectors/selectorIsImportBatchFullyImported";
import { selectorIsImportBatchPartiallyImported } from "../../selectors/selectorIsImportBatchPartiallyImported";
import { selectorIsImportBatchUploadFinished } from "../../selectors/selectorIsImportBatchUploadFinished";
import { selectorIsImportBatchUploadInProgress } from "../../selectors/selectorIsImportBatchUploadInProgress";
import {
  PAYABLE_BATCH_DELETE_DESCRIPTION,
  PAYABLE_BATCH_DELETE_EMPTY_DESCRIPTION,
  PAYABLE_ENTITY_LABEL,
  PAYABLE_ENTITY_LABEL_PLURAL_LOWER
} from "./constants";

type Props = {
  batch: Batch;
  /** Resolved before the page renders, so the row counts are never unknown. */
  batchSummary: BatchSummary;
  downloadExampleCSV: () => void;
  downloadErrorsCSV: () => void;
  onDeleted: () => void;
};

export const PayableBatchStatusWidget: React.FC<Props> = ({
  batch,
  batchSummary,
  downloadExampleCSV,
  downloadErrorsCSV,
  onDeleted
}) => {
  const batchId = batch.id as string;
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();
  const skippedRowsCount = useSkippedRowsCount(batch, PAYABLES_SHEET_SLUG);
  const payablesColumnsGuideModal = useModalPayablesColumnsGuide();

  const progress = batchSummary.progress;
  const breakdown = batchSummary.breakdown;
  const itemCount = progress?.total;
  const failedCount = progress?.failed;
  const isEmptyBatch = itemCount === 0;

  const { failedBatchItems } = useFailedBatchItems({ batch, failedCount });
  const { isFailed: isUploadFailed } = useFlatfileUploadJobStatus(batch);
  const { pill } = useImportBatchStatus(batchId, true);

  const { menuItems, buttons } = useImportBatchStatusActions({
    batch,
    isEmptyBatch,
    failedCount,
    entityLabel: PAYABLE_ENTITY_LABEL,
    entityLabelPlural: PAYABLE_ENTITY_LABEL_PLURAL_LOWER,
    deleteDescription: PAYABLE_BATCH_DELETE_DESCRIPTION,
    deleteEmptyBatchDescription: PAYABLE_BATCH_DELETE_EMPTY_DESCRIPTION,
    downloadExampleCSV,
    downloadErrorsCSV,
    onDeleted,
    hideButtonOn: false
  });

  const isBatchProcessing =
    batch.status === BatchStatus.PENDING ||
    batch.status === BatchStatus.PROCESSING;

  const goToPayables = (tab: string) => {
    const search = toURLQuerySearch({ filter: { bulkBatchId: batchId } });
    const pathname =
      tab === "deductions"
        ? PATH_PAYABLES_DEDUCTIONS
        : PATH_PAYABLES_TAB.replace(":tab", tab);

    history.push({ pathname, search });
  };

  const renderStats = (showSeeAll: boolean = true) => (
    <WSList gap="XL">
      <WSList gap="S">
        <WSFlexBox.CenterY justify="space-between">
          <WSText.Paragraph weight="medium" color="gray600">
            Payables
          </WSText.Paragraph>
          {showSeeAll && (
            <WSButton.Link
              rightIcon="chevron-right"
              onClick={() => goToPayables("all")}
            >
              See all
            </WSButton.Link>
          )}
        </WSFlexBox.CenterY>
        <WSDataItem
          inline
          label={{ text: "Line items count" }}
          value={
            breakdown && "payablesCount" in breakdown
              ? breakdown.payablesCount
              : "--"
          }
        />
        <WSDataItem
          inline
          label="Total payables"
          value={
            breakdown && "payablesAmount" in breakdown
              ? breakdown.payablesAmount
              : "--"
          }
          formatterOptions={{ currency: defaultCurrency }}
        />
      </WSList>

      <WSList gap="S">
        <WSFlexBox.CenterY justify="space-between">
          <WSText.Paragraph weight="medium" color="gray600">
            Deductions
          </WSText.Paragraph>
          {showSeeAll && (
            <WSButton.Link
              rightIcon="chevron-right"
              onClick={() => goToPayables("deductions")}
            >
              See all
            </WSButton.Link>
          )}
        </WSFlexBox.CenterY>
        <WSDataItem
          inline
          label={{ text: "Deductions created" }}
          value={
            breakdown && "deductionsCount" in breakdown
              ? breakdown.deductionsCount
              : "--"
          }
        />
        <WSDataItem
          inline
          label="Total deductions"
          value={
            breakdown && "deductionsAmount" in breakdown
              ? breakdown.deductionsAmount
              : "--"
          }
          formatterOptions={{ currency: defaultCurrency }}
        />
      </WSList>

      <WSList gap="S">
        <WSFlexBox.CenterY justify="space-between">
          <WSText.Paragraph weight="medium" color="gray600">
            Import total
          </WSText.Paragraph>
          <WSText.Paragraph weight="medium" color="gray700">
            {breakdown && "netAmount" in breakdown
              ? formatMoney(breakdown.netAmount, defaultCurrency)
              : "--"}
          </WSText.Paragraph>
        </WSFlexBox.CenterY>
        <WSDataItem
          inline
          label={{ text: "Total items uploaded" }}
          value={itemCount ?? "--"}
        />
        {/* Rows the upload skipped never became batch items, so they were never imported. */}
        {skippedRowsCount > 0 && (
          <WSFlexBox.CenterY justify="space-between">
            <WSFlexBox.CenterY gap="XS">
              <WSText.ParagraphSm color="gray500">
                Items skipped
              </WSText.ParagraphSm>
              <WSIcon name="alert" size="S" color="amber400" />
            </WSFlexBox.CenterY>
            <WSText.ParagraphSm>{skippedRowsCount}</WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        )}
      </WSList>
    </WSList>
  );

  const renderErrorsFound = () => (
    <WSAlert icon="alert" theme="warning" title="Errors found">
      <ul>
        {failedBatchItems.map(failedItem => (
          <li key={failedItem.id}>
            {pluralize("item", failedItem.value, true)}: {failedItem.label}
          </li>
        ))}
      </ul>
    </WSAlert>
  );

  const renderBatchStates = () => {
    if (selectorIsImportBatchFullyImported({ batch, failedCount })) {
      return (
        <WSList gap="XL">
          <ImportBatchProgress batchId={batchId} />
          {skippedRowsCount > 0 ? (
            <ImportBatchUploadUnuploadedRowsAlert
              batch={batch}
              sheetSlug={PAYABLES_SHEET_SLUG}
              entityLabel={PAYABLE_ENTITY_LABEL}
            />
          ) : null}
          {renderStats()}
        </WSList>
      );
    }

    if (selectorIsImportBatchPartiallyImported({ batch, failedCount })) {
      return (
        <WSList gap="L">
          <ImportBatchProgress batchId={batchId} />
          {renderErrorsFound()}
          {renderStats()}
        </WSList>
      );
    }

    if (batch.status === BatchStatus.FAILED) {
      return (
        <WSList gap="L">
          <ImportBatchProgress batchId={batchId} />
          {renderErrorsFound()}
          {renderStats(false)}
        </WSList>
      );
    }

    if (isBatchProcessing) {
      return (
        <WSList gap="L">
          <ImportBatchProgress batchId={batchId} />
          {renderStats(false)}
        </WSList>
      );
    }

    if (selectorIsImportBatchUploadFinished({ batch, isEmptyBatch })) {
      return (
        <WSList gap="L">
          <WSFlexBox.CenterY>
            <WSIcon name="details" mr="XS" />
            <WSText.ParagraphSm weight="book" color="gray600">
              A spreadsheet is uploaded and ready to be imported and processed
              as payables.
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>

          {skippedRowsCount > 0 ? (
            <ImportBatchUploadRejectionsAlert
              batch={batch}
              sheetSlug={PAYABLES_SHEET_SLUG}
              entityLabel={PAYABLE_ENTITY_LABEL}
            />
          ) : (
            <WSAlert icon="bulb" size="M" theme="info" title="Pro tip">
              View upload to check that all information is accurate prior to
              completing the import process. If there are any changes to make to
              the spreadsheet, delete this import and start a new batch after
              you’ve updated your spreadsheet.
            </WSAlert>
          )}

          {renderStats(false)}
        </WSList>
      );
    }

    if (selectorIsImportBatchUploadInProgress(batch)) {
      // The upload job died mid-flight, the batch will never finish uploading.
      if (isUploadFailed) {
        return <ImportBatchUploadFailedAlert />;
      }

      return (
        <WSList gap="L">
          <ImportBatchUploadProgress batch={batch} />
          <WSFlexBox wrap="nowrap">
            <WSIcon size="S" name="time" mr="XS" />
            <WSText.ParagraphSm weight="book" color="gray600">
              A spreadsheet is being uploaded. If you close this page, the
              progress will continue. Ensure you complete the last step to
              import and process the spreadsheet once it has been uploaded.
            </WSText.ParagraphSm>
          </WSFlexBox>
        </WSList>
      );
    }

    if (isEmptyBatch && selectorIsImportBatchFileUploaded(batch)) {
      return (
        <WSFlexBox wrap="nowrap">
          <WSIcon name="details" mr="XS" />
          <WSText.ParagraphSm weight="book" color="gray600">
            A spreadsheet is selected but not uploaded yet. Click the view
            upload button to upload your formatted spreadsheet.
          </WSText.ParagraphSm>
        </WSFlexBox>
      );
    }

    if (isEmptyBatch && !selectorIsImportBatchFileUploaded(batch)) {
      return (
        <WSEmptyState
          hasBorder={false}
          orientation="horizontal"
          size="M"
          type="documents"
          title="File not uploaded"
          description={
            <WSText.ParagraphSm weight="book" color="gray600" inline>
              Upload your formatted spreadsheet to the batch. Need help setting
              up your spreadsheet?
              <WSButton.Link onClick={() => payablesColumnsGuideModal.open()}>
                Learn more here.
              </WSButton.Link>
            </WSText.ParagraphSm>
          }
        />
      );
    }

    return null;
  };

  return (
    <WSCard
      header={{
        label:
          selectorImportBatchUploadedFilename({
            batch,
            entityLabel: PAYABLE_ENTITY_LABEL
          }) || "Upload spreadsheet",
        value: { pill }
      }}
      actions={{
        orientation: { xs: "vertical", s: "horizontal" },
        alignment: "fill",
        menuItems,
        buttons
      }}
      divider
    >
      {renderBatchStates()}
    </WSCard>
  );
};
